// Eskom PARCC — fold messy raw field values onto the canonical filter
// vocabulary the project owner specified in `feedback/Filters.xlsx`.
//
// The register data carries dozens of mechanism spellings and a few division
// variants (Gx vs GX – Head Office, ICT vs ICT & Professional Services). The
// filter dropdowns must show the owner's clean controlled vocabulary AND still
// match the raw values stored on transactions — so each accessor passes its
// value through one of these normalisers before comparing/aggregating.
//
// Pure, no React. Unit-tested in test/domain/normalizeFacets.test.ts.
import { canonicalDivision } from './divisions';

/** The 14 mechanism options from Filters.xlsx (the controlled filter vocabulary). */
export const FILTER_MECHANISM_OPTIONS = [
  'LPO',
  'Formal Tendering',
  'Formal Tendering - Open Tender',
  'Formal Tendering - Limited Tender Closed',
  'Informal Tendering',
  'Restricted Tendering - Single Source',
  'Restricted Tendering - Sole Source',
  'Contract Adjustment',
  'Emergency Procurement',
  'Modification',
  'Condonation',
  'Urgent Procurement',
  'Transversal/formal',
  'Multiple Sources',
] as const;

/** The 6 division options from Filters.xlsx. */
export const FILTER_DIVISION_OPTIONS = [
  'NTCSA',
  'ICT & Professional Services',
  'Tactical Sourcing',
  'GCD',
  'PED',
  'Gx',
] as const;

/**
 * The dataset filter options. Filters.xlsx specified four (Contracts /
 * POs / LPOs / Task Order / Other); R6 splits the merged PO-LPO bucket and adds
 * Panels, matching how the client deck has always reported them. The singular
 * "Task Order" spelling is retained — it is what the owner's sheet says, and
 * {@link normalizeDatasetFacet} folds every task-order variant onto it.
 */
export const FILTER_DATASET_OPTIONS = [
  'Contracts',
  'Panels',
  'POs',
  'LPOs',
  'Task Order',
  'Other',
] as const;

/** The 4 period options from Filters.xlsx. */
export const FILTER_PERIOD_OPTIONS = ['Q1', 'Q2', 'Q3', 'Q4'] as const;

const norm = (s: string | null | undefined): string =>
  String(s ?? '')
    .trim()
    .toLowerCase();

/** Case-insensitive exact-match against a canonical list; returns the canonical spelling. */
function exact<T extends string>(value: string, canonical: readonly T[]): T | undefined {
  const n = norm(value);
  return canonical.find((c) => norm(c) === n);
}

/**
 * Fold a raw mechanism onto one of {@link FILTER_MECHANISM_OPTIONS}. Exact
 * (case-insensitive) canonical values win; otherwise keyword heuristics map the
 * 40+ raw variants. Genuine outliers return their trimmed original so the hybrid
 * option list still surfaces them; blanks return ''.
 */
export function normalizeMechanismFacet(raw: string | null | undefined): string {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return '';
  const canonical = exact(trimmed, FILTER_MECHANISM_OPTIONS);
  if (canonical) return canonical;

  const n = norm(trimmed);
  if (n.includes('multiple source')) return 'Multiple Sources';
  if (n.includes('sole source')) return 'Restricted Tendering - Sole Source';
  if (n.includes('single source')) return 'Restricted Tendering - Single Source';
  if (n.includes('condonation')) return 'Condonation';
  if (n.includes('contract adjustment')) return 'Contract Adjustment';
  if (n.includes('modification')) return 'Modification';
  if (n.includes('transvers') || n.includes('transves')) return 'Transversal/formal';
  if (n.includes('emergency')) return 'Emergency Procurement';
  if (n.includes('urgent')) return 'Urgent Procurement';
  if (n.includes('open tender')) return 'Formal Tendering - Open Tender';
  if (n.includes('limited tender') || n.includes('closed tender'))
    return 'Formal Tendering - Limited Tender Closed';
  if (n.includes('informal')) return 'Informal Tendering';
  if (n.includes('lpo') || n.includes('local purchase')) return 'LPO';
  if (n.includes('formal')) return 'Formal Tendering';
  return trimmed;
}

/** Fold a raw division onto its canonical label (shared with scoping + display). */
export function normalizeDivisionFacet(raw: string | null | undefined): string {
  return canonicalDivision(raw);
}

/**
 * Fold a raw dataset onto one of {@link FILTER_DATASET_OPTIONS}. Every task-order
 * variant — the plain R6 'Task Orders' and the retired 'NTCSA Task Orders' /
 * 'ICT Task Orders' — collapses onto the owner's singular 'Task Order'.
 *
 * The retired 'POs / LPOs' is deliberately left ALONE: it cannot be split without
 * the row's reference number, and passing it through verbatim makes it show up as
 * a visible outlier in the hybrid dropdown (see buildFacetOptions), which is how
 * an incomplete `splitDatasetBuckets` migration announces itself. Folding it into
 * either bucket would hide the problem and mis-state the numbers.
 */
export function normalizeDatasetFacet(raw: string | null | undefined): string {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return '';
  if (norm(trimmed).includes('task order')) return 'Task Order';
  return trimmed;
}

/** Map a boolean-ish flag to the Yes/No filter vocabulary; absent counts as No. */
export function yesNo(value: boolean | null | undefined): 'Yes' | 'No' {
  return value === true ? 'Yes' : 'No';
}
